/* LEGEND — ChartPane (lightweight-charts v5). Ported from chart.jsx.
   Historical from the data source; live candle from the sim (MOCK) or quote mids (live). */
import React from 'react';
import {
  createChart, CandlestickSeries, LineSeries, HistogramSeries, LineStyle, CrosshairMode,
  createSeriesMarkers,
} from 'lightweight-charts';
import { VolPulse, VolPulseReadout, ReadBus } from './VolPulse';
import { isForming, paceFrac, type FormingOf } from '../lib/vol-pulse';
import { emaArr, smaArr, vwapBands, makeTailComputer } from '../lib/format';
import { levelLabel, mergeLabels } from '../lib/level-label';
import { TF_MIN, TF_TO_SERVER, EMA_PERIODS, EMA_WIDTH, MA_KIND_COLOR } from '../lib/constants';
import { LiveCandles, type FormingSnap, type Print, type Touch } from '../lib/forming';
import { VwapBandsPrimitive } from '../lib/vwap-bands';
import { MOCK } from '../lib/mode';
import { META } from '../data/mock';
import { getCandles, getIndicators, getBuyScores, getForming, getLadder, subscribeForming, subscribeLadder, subscribeQuotes, subscribeTrades } from '../data/source';

/* Buy-score band → bar colour. The BAND comes from the service (one scoring
   definition, shared with the ladder gauge); the client only paints it, so the
   chart can never disagree with the gauge about who won a bar. Saturation carries
   conviction: the balanced grey recedes, the extremes read at a glance. */
/* Font for the on-chart marker labels (the AI level reads: SWL·1D, E21·5m).
   Deliberately below layout.fontSize (11), which stays the price/time axis
   size — see the options() note in the chart setup for why the two can
   differ at all. */
const MARKER_FONT_PX = 9;

const BAND_COLOR: Record<string, string> = {
  strong: 'rgba(56,192,138,0.88)',
  buy: 'rgba(56,192,138,0.46)',
  balanced: 'rgba(123,130,140,0.36)',
  sell: 'rgba(236,106,106,0.46)',
  heavy: 'rgba(236,106,106,0.85)',
};

/* Chart flags. The ladder FSM states (BC / TURN / SR / FLIP / FF / FEED /
   RTR / S / P) and the ignition + pullback markers are gone from the chart by
   request: the marker series now carries ONLY the model (vision) fires that
   ride the ladder-signal channel. The ladder card, its badges and the
   watchlist rows keep the full FSM story. */
const FLAG_DEF: Record<string, { position: 'aboveBar' | 'belowBar'; shape: 'arrowUp' | 'arrowDown' | 'circle'; color: string; text: string }> = {
  // Model signal lane fires (MV_HOLD_S/R): circles, not arrows — a level-hold
  // claim, not a momentum call. Gated in pushSig to p≥0.90 + tape-informed so
  // the chart gets a handful a day, not the lane's full firehose.
  // Text is REPLACED per-signal with the level's own short name (levelLabel):
  // every marker here is already gated to p>=0.90 AND tape-informed, so the
  // probability is near-constant and carries nothing — which level held is the
  // variable the reader wants. 'AI' named the speaker, not the claim.
  // Side stays encoded by colour + above/below only: this lightweight-charts
  // build accepts 'circle' | 'arrowUp' | 'arrowDown' and no 'square', and an
  // arrow would read as a momentum call, which a level-hold claim is not.
  MV_HOLD_S: { position: 'belowBar', shape: 'circle', color: '#38c08a', text: 'AI' },
  MV_HOLD_R: { position: 'aboveBar', shape: 'circle', color: '#ec6a6a', text: 'AI' },
};
const FLAGS_CAP = 300; // matches the server's :signals log cap

const { useRef, useEffect } = React;
const padBars = (px: number) => (px > 0 ? Math.ceil(px / 7) + 1 : 2);   // right offset (bars) that clears overlays docked at the axis

export function ChartPane({ symbol, tf, indicators, chartApiRef, colors, onPrice, onChartClick, onHover, live = true, padRight = 0 }: any) {
  const hostRef = useRef<any>(null);
  const chartRef = useRef<any>(null);
  const refs = useRef<any>({});
  const dataRef = useRef<any>({ candles: [], tfMin: 1, rng: Math.random });
  const tickRef = useRef<any>(null);
  // Buy-score refresh timer. Kept OFF refs.current on purpose: the rebuild
  // teardown passes every value there to chart.removeSeries().
  const bsPollRef = useRef<any>(null);
  // Volume Pulse (VolPulse.tsx): the pane's driver, its 1 s clock (the pace decays with no prints; a bar closes with
  // none) and the readout's feed. Off refs.current for the same reason as bsPollRef.
  const pulseRef = useRef<VolPulse | null>(null);
  const pulseClockRef = useRef<any>(null);
  const rvBus = useRef<ReadBus | null>(null);
  if (!rvBus.current) rvBus.current = new ReadBus();
  const bandsRef = useRef<any>(null);
  const cbRef = useRef<any>({});
  cbRef.current = { onPrice, onChartClick, onHover };
  // entry marks (EntryMarksLayer): colour the last-price tag — a label-only price line stands in for the native last value
  const tagRef = useRef<{ color: string | null; line: any }>({ color: null, line: null });
  const padRef = useRef<number>(padRight); padRef.current = padRight;
  const applyTag = () => {
    const s = refs.current.candle, tg = tagRef.current; if (!s) return;
    if (tg.line) { try { s.removePriceLine(tg.line); } catch (e) {} tg.line = null; }
    try { s.applyOptions({ lastValueVisible: !tg.color }); } catch (e) {}
    const arr = dataRef.current.candles; if (!tg.color || !arr.length) return;
    try { tg.line = s.createPriceLine({ price: arr[arr.length - 1].close, color: tg.color, lineVisible: false, axisLabelVisible: true, axisLabelColor: tg.color, axisLabelTextColor: '#0a0b0d', title: '' }); }
    catch (e) { tg.line = null; try { s.applyOptions({ lastValueVisible: true }); } catch (e2) {} }
  };
  // the tag follows the last close (the chart's live feed calls this after every candle update)
  const syncTag = (px: number) => {
    const tg = tagRef.current;
    if (tg.line) { try { tg.line.applyOptions({ price: px }); } catch (e) {} }
    else if (tg.color) applyTag();
  };

  // ---- create chart once ----
  useEffect(() => {
    const c = colors;
    const chart = createChart(hostRef.current, {
      autoSize: false,
      layout: {
        background: { type: 'solid', color: c.bg } as any,
        textColor: c.text,
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 11,
        attributionLogo: false,
        panes: { separatorColor: c.line, separatorHoverColor: c.line2 },
      } as any,
      grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
      rightPriceScale: { borderColor: c.line, scaleMargins: { top: 0.08, bottom: 0.08 }, entireTextOnly: true, minimumWidth: 64 },   // = --axis-w, so the rail + range column dock flush
      // rightOffset 7 bars (49px at the default spacing) keeps the live candle clear of the levels rail, which is 36px
      // wide on the MV-5 lane (two reads side by side, StructureMap `lanes2`); at 5 bars it sat under the rail's edge.
      timeScale: { borderColor: c.line, timeVisible: true, secondsVisible: false, rightOffset: 7, barSpacing: 7 },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: c.cross, width: 1, style: LineStyle.Dashed, labelBackgroundColor: c.surface3 } as any,
        horzLine: { color: c.cross, width: 1, style: LineStyle.Dashed, labelBackgroundColor: c.surface3 } as any,
      },
      handleScroll: { vertTouchDrag: false },
    });
    chartRef.current = chart;

    // ---- marker text size (the AI level labels: SWL·1D, E21·5m, SWH·1W) ----
    // The series-markers plugin has no font of its own. SeriesMarkerBase carries
    // only `size`, which scales the SHAPE and not the text, and SeriesMarkersOptions
    // only `autoScale`/`zOrder` — so the labels ride the chart-wide layout.fontSize,
    // which is also the price and time axis size.
    //
    // They are not, however, on the same code path. The plugin's pane view reads the
    // PUBLIC `chart.options().layout` on every paint; the axes read the model's
    // internal options. Narrowing what the public getter reports therefore reaches
    // the markers and nothing else. Nothing in this app calls chart.options(), and
    // our own primitive (VwapBandsPrimitive) does not either.
    //
    // Failure mode if a future lightweight-charts moves the plugin onto the internal
    // options: the labels quietly go back to layout.fontSize. Cosmetic, not a trap.
    // Memoised on the real options object — a stable reference until applyOptions
    // replaces it — so this allocates once, not once per paint.
    const realChartOptions = chart.options.bind(chart);
    let optIn: any = null, optOut: any = null;
    (chart as any).options = () => {
      const o: any = realChartOptions();
      if (o !== optIn) {
        optIn = o;
        optOut = { ...o, layout: { ...o.layout, fontSize: MARKER_FONT_PX } };
      }
      return optOut;
    };

    const isSized = () => {
      const elr = hostRef.current; if (!elr || !elr.clientWidth) return true;
      const cv = elr.querySelector('canvas');
      return !!(cv && cv.width >= elr.clientWidth * (window.devicePixelRatio || 1) * 0.9);
    };
    let tries = 0;
    const tick = () => {
      const elr = hostRef.current;
      if (elr && elr.clientWidth) {
        const w = elr.clientWidth, h = elr.clientHeight;
        if (isSized()) { chart.resize(w, h); clearInterval(sizeIv); return; }
        chart.resize((tries % 2 === 0) ? w - 2 : w, h);
      }
      if (++tries >= 40) clearInterval(sizeIv);
    };
    tick();
    const sizeIv = setInterval(tick, 110);
    const ro = new ResizeObserver(() => { tries = 0; tick(); });
    ro.observe(hostRef.current);

    chart.subscribeClick((param: any) => {
      if (!param.point || !refs.current.candle) return;
      const price = refs.current.candle.coordinateToPrice(param.point.y);
      if (price == null) return;
      cbRef.current.onChartClick && cbRef.current.onChartClick({ price, x: param.point.x, y: param.point.y });
    });
    chart.subscribeCrosshairMove((param: any) => {
      if (pulseRef.current) pulseRef.current.hover(param.point && param.time != null ? Number(param.time) : null);
      if (!param.point) { cbRef.current.onHover && cbRef.current.onHover(null); return; }
      const cs = refs.current.candle;
      const price = cs ? cs.coordinateToPrice(param.point.y) : null;
      const ohlc = param.seriesData.get(cs);
      const vals: any = {};
      ['vwap', 'ema9', 'ema21', 'ema50', 'ema200', 'sma9', 'sma21', 'sma50', 'sma200'].forEach((k) => { if (refs.current[k]) { const d = param.seriesData.get(refs.current[k]); if (d) vals[k] = d.value; } });
      cbRef.current.onHover && cbRef.current.onHover({ price, y: param.point.y, x: param.point.x, ohlc, vals });
    });

    chartApiRef.current = {
      priceToY: (p: number) => { const s = refs.current.candle; return s ? s.priceToCoordinate(p) : null; },
      yToPrice: (y: number) => { const s = refs.current.candle; return s ? s.coordinateToPrice(y) : null; },
      getLast: () => dataRef.current.candles.length ? dataRef.current.candles[dataRef.current.candles.length - 1].close : null,
      // The bars the chart is drawing right now (live-mutated: the forming bar
      // extends in place, new bars push). Read-only by contract — overlays
      // must never write into it.
      getCandles: () => ({ symbol: dataRef.current.symbol, tf: dataRef.current.tf, candles: dataRef.current.candles }),
      // entry marks: the last-price tag's colour (null = the native label back)
      setTag: (col: string | null) => {
        const tg = tagRef.current; col = col || null; if (tg.color === col) return;
        tg.color = col;
        if (tg.line && col) { try { tg.line.applyOptions({ color: col, axisLabelColor: col }); return; } catch (e) {} }
        applyTag();
      },
      chart,
    };

    return () => {
      ro.disconnect(); clearInterval(sizeIv);
      if (tickRef.current) clearInterval(tickRef.current);
      if (bsPollRef.current) clearInterval(bsPollRef.current);
      if (pulseClockRef.current) clearInterval(pulseClockRef.current);
      if (pulseRef.current) { pulseRef.current.dispose(); pulseRef.current = null; }
      chart.remove(); chartRef.current = null;
    };
  }, []);

  // ---- (re)build series on symbol / tf / indicator change ----
  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    const c = colors;
    let cancelled = false;
    let cleanupLive = () => {};
    let cleanupFlags = () => {};

    // Live tail: extend each shown indicator line to the current (forming) bar.
    // Incremental: closed bars fold into prefix state once per bar boundary, so a
    // tick inside the forming bar costs O(1) instead of a full-array recompute —
    // same values (identical recurrence + rounding), just without the per-print
    // O(bars) work + allocation churn that stalled the UI on a fast tape.
    // Re-made once the server's indicator rows are in, seeded from the newest one (no EMA seam).
    let tails = makeTailComputer(EMA_PERIODS);
    function refreshTails(arr: any[]) {
      const t = tails(arr);
      if (!t) return;
      for (const key of Object.keys(EMA_PERIODS)) {
        if (refs.current[key]) refs.current[key].update(t.ema[key]);
        const N = EMA_PERIODS[key];
        const smaKey = 'sma' + N;
        if (refs.current[smaKey] && arr.length >= N) {
          let sum = 0;
          for (let i = arr.length - N; i < arr.length; i++) sum += arr[i].close;
          refs.current[smaKey].update({ time: arr[arr.length - 1].time, value: +(sum / N).toFixed(4) });
        }
      }
      // VWAP center + bands share one session computation, so they stay aligned.
      if (refs.current.vwap) refs.current.vwap.update({ time: t.band.time, value: t.band.vwap });
      if (bandsRef.current) bandsRef.current.updateLast(t.band);
    }

    // ---- live (real data): subscribe BEFORE the hydrate (mv5-prod/05 §3) ----
    // Trade prints and the screener's forming frames are buffered from now until the chart is built, so nothing
    // between the hydrate's snapshot and the first live message is lost. Quote mids only drive the displayed price
    // until the first trade — they never make or move a candle.
    const serverTf = TF_TO_SERVER[tf] || '1Min';
    const pickSnap = (f: any): FormingSnap => ({
      asOf: f && typeof f.asOf === 'number' ? f.asOf : undefined,
      wm: f && f.wm ? f.wm : null,
      forming: f && f.forming ? (f.forming[serverTf] ?? null) : null,
      lastClosed: f && f.lastClosed ? (f.lastClosed[serverTf] ?? null) : null,
    });
    const early: { prints: Print[]; snaps: FormingSnap[]; onPrint: ((p: Print) => void) | null; onSnap: ((s: FormingSnap) => void) | null } =
      { prints: [], snaps: [], onPrint: null, onSnap: null };
    if (live && !MOCK) {
      let hasTrade = false;
      let offQuotes: (() => void) | null = null;
      const offTrades = subscribeTrades(symbol, (t: any) => {
        if (!hasTrade) {
          hasTrade = true;
          // trades are authoritative once they arrive: drop the (chatty) quote channel entirely
          if (offQuotes) { offQuotes(); offQuotes = null; }
        }
        const p: Print = { ts: t.timestamp ? new Date(t.timestamp).getTime() : Date.now(), price: Number(t.price), size: Number(t.size) || 0 };
        if (early.onPrint) early.onPrint(p);
        else if (early.prints.push(p) > 50_000) early.prints.splice(0, 10_000);   // a slow hydrate on a fast tape
      });
      const offForming = subscribeForming(symbol, (f: any) => {
        const sn = pickSnap(f);
        if (early.onSnap) early.onSnap(sn); else early.snaps.push(sn);
      });
      offQuotes = subscribeQuotes(symbol, (q: any) => {
        if (hasTrade) return;
        const mid = Number(q.mid);
        if (Number.isFinite(mid)) cbRef.current.onPrice && cbRef.current.onPrice(mid);
      });
      cleanupLive = () => { offTrades(); offForming(); if (offQuotes) { offQuotes(); offQuotes = null; } };
    }

    (async () => {
      const [candles, indHistory, buyScores, formingSec] = await Promise.all([
        getCandles(symbol, tf), getIndicators(symbol, tf), getBuyScores(symbol, tf),
        live && !MOCK ? getForming(symbol, tf) : Promise.resolve(null),
      ]);
      if (cancelled || !chartRef.current) return;
      // zero closed bars: the chart still goes live when the screener has a forming bar (or prints arrive)
      if (!candles.length && !(live && !MOCK)) return;

      if (bandsRef.current && refs.current.candle) { try { refs.current.candle.detachPrimitive(bandsRef.current); } catch (e) {} }
      bandsRef.current = null;
      if (pulseClockRef.current) { clearInterval(pulseClockRef.current); pulseClockRef.current = null; }
      if (pulseRef.current) { pulseRef.current.dispose(); pulseRef.current = null; }
      Object.values(refs.current).forEach((s: any) => { try { chart.removeSeries(s); } catch (e) {} });
      refs.current = {};
      // Stop the PREVIOUS build's buy-score poll here, alongside the series it fed.
      // It used to be cleared further down with the tick loop — i.e. AFTER this
      // build had already armed its own poll — so every rebuild killed the interval
      // it had just created and the pane sat frozen until the next full rebuild.
      if (bsPollRef.current) { clearInterval(bsPollRef.current); bsPollRef.current = null; }

      dataRef.current.candles = candles;
      dataRef.current.tfMin = TF_MIN[tf] || 1;
      // Tag the array with its owner so overlays reading it (TrendLineLayer)
      // can refuse bars from a symbol/tf they were not mounted for.
      dataRef.current.symbol = symbol;
      dataRef.current.tf = tf;
      let seed = 0; for (const ch of (symbol + tf)) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
      dataRef.current.rng = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };

      const refPx = candles.length ? candles[0].close : Number(formingSec?.forming?.c ?? formingSec?.lastClosed?.c ?? 1);
      const candle = chart.addSeries(CandlestickSeries, {
        upColor: c.up, downColor: c.down, borderVisible: false,
        wickUpColor: c.up, wickDownColor: c.down,
        priceLineVisible: true, priceLineColor: c.line2, priceLineStyle: LineStyle.Dotted,
        lastValueVisible: true,
        priceFormat: { type: 'price', precision: refPx < 1 ? 4 : 2, minMove: refPx < 1 ? 0.0001 : 0.01 },
      });
      candle.setData(candles.map((d: any) => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close })));
      refs.current.candle = candle;
      tagRef.current.line = null; applyTag();

      // EMA lines from the SERVER indicators API (real snapshots); when the API has
      // no data (MOCK / aggregated 3m) fall back to computing from the candles. Each
      // EMA renders only when its data is available and is hidden gracefully otherwise
      // (empty series → not added), e.g. EMA200 needs at least 200 bars. Line weight
      // scales with the period: 9→1px, 21→2px, 50→3px, 200→4px.
      const useApi = indHistory.length > 0;
      tails = makeTailComputer(EMA_PERIODS, useApi ? indHistory[indHistory.length - 1] : null);
      const emaData = (key: string): any[] => {
        if (useApi) return indHistory.map((h: any) => ({ time: h.time, value: h.values?.[key] })).filter((p: any) => p.value != null);
        const period = EMA_PERIODS[key] || 9;
        return candles.length >= period ? emaArr(candles, period) : [];
      };
      const ind = indicators || {};
      // Two independent sets, overlayable: EMAs (blue) and simple MAs (amber),
      // each all four periods; width says which average (1/2/3/4px), color
      // says which kind. Back-compat: maKind or per-ema boolean shapes map in.
      const emasOn = ind.emas != null ? !!ind.emas
        : ind.maKind ? ind.maKind === 'ema'
        : !!(ind.ema9 || ind.ema21 || ind.ema50 || ind.ema200);
      const masOn = ind.mas != null ? !!ind.mas : ind.maKind === 'ma';
      const addKindLine = (kind: 'ema' | 'ma', key: string) => {
        const period = EMA_PERIODS[key] || 9;
        const refKey = kind === 'ema' ? key : 'sma' + period;
        const arr = kind === 'ema' ? emaData(key)
          : candles.length >= period ? smaArr(candles, period) : [];
        if (!arr.length) return; // gracefully hide when the indicator isn't available
        const s = chart.addSeries(LineSeries, { color: MA_KIND_COLOR[kind], lineWidth: EMA_WIDTH[key], priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: true, crosshairMarkerRadius: 3 });
        s.setData(arr);
        refs.current[refKey] = s;
      };
      if (emasOn) for (const key of Object.keys(EMA_PERIODS)) addKindLine('ema', key);
      if (masOn) for (const key of Object.keys(EMA_PERIODS)) addKindLine('ma', key);
      // VWAP: session center line + ±1σ/±2σ alpha bands (dotted edges, drawn behind
      // the candles). Center and bands come from one session computation so the line
      // sits exactly on the bands' midline.
      if (ind.vwap) {
        const bands = vwapBands(candles);
        if (bands.length) {
          const s = chart.addSeries(LineSeries, { color: c.warn, lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: true, crosshairMarkerRadius: 3 });
          s.setData(bands.map((b: any) => ({ time: b.time, value: b.vwap })));
          refs.current.vwap = s;
          if (bands.length > 1 && refs.current.candle) {
            const prim = new VwapBandsPrimitive({
              fill1: 'rgba(224,177,90,0.10)', fill2: 'rgba(224,177,90,0.05)', edge: 'rgba(224,177,90,0.45)',
            });
            refs.current.candle.attachPrimitive(prim);
            prim.setData(bands);
            bandsRef.current = prim;
          }
        }
      }

      // ---- sub-pane 1: BUY SCORE (1-100, 50 = balanced) ----
      // Each bucket is scored ONCE, service-side, from the SUMMED aggressor flow of
      // its minutes (dollars aggregate, scores do not) — the exact same definition the
      // ladder gauge shows live, so the two can never disagree. The client contributes
      // nothing but the band → colour mapping. Fixed 0-100 scale (a bar's height is
      // comparable across symbols and days) with a dotted midline at 50.
      // No scores — unsupported timeframe, MOCK, or a symbol whose tape the service
      // never witnessed — simply means no pane, never a zero-filled fake.
      if (ind.buyscore && buyScores.length) {
        const bs = chart.addSeries(HistogramSeries, {
          priceFormat: { type: 'price', precision: 0, minMove: 1 },
          priceScaleId: '', lastValueVisible: true, priceLineVisible: false, base: 0,
          autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
        }, 1);
        bs.setData(buyScores.map((p: any) => ({ time: p.time, value: p.value, color: BAND_COLOR[p.band] || BAND_COLOR.balanced })));
        bs.priceScale().applyOptions({ scaleMargins: { top: 0.12, bottom: 0 } });
        try {
          bs.createPriceLine({ price: 50, color: 'rgba(123,130,140,0.45)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
        } catch (e) {}
        refs.current.buyscore = bs;

        // Keep it advancing. The score is computed service-side and rewritten
        // once per symbol per minute, and there is no buy-score WS channel — so
        // without a refresh this pane is frozen at build time while the candles,
        // the volume pane and the live ladder gauge all keep moving. Leaving a
        // growing empty tail would be indistinguishable from the feature's own
        // gap semantics ("a minute the service did not witness"), which is
        // exactly the chart-vs-gauge disagreement this design forbids.
        // setData (not update) because a late-arriving minute can backfill a gap
        // anywhere in the series, not only at the tail; the array is ~400 points.
        // Still ZERO client-side scoring — it re-renders what the server sent.
        bsPollRef.current = setInterval(() => {
          getBuyScores(symbol, tf, { fresh: true }).then((pts: any[]) => {
            if (cancelled || refs.current.buyscore !== bs || !pts.length) return;
            bs.setData(pts.map((p: any) => ({ time: p.time, value: p.value, color: BAND_COLOR[p.band] || BAND_COLOR.balanced })));
          }).catch(() => { /* transient — the next tick retries */ });
        }, 20_000);
      }

      // ---- sub-pane 2: VOLUME PULSE (lib/vol-pulse.ts), below the score ----
      // Each bar's volume ÷ the median of the 10 bars before it, one line around a flat median; the forming bar
      // dashes to its pace. Sits on pane 1 when the score pane wasn't created, so an unavailable score leaves no
      // empty gap between the candles and the pulse.
      // Forming = the last bar's ET bucket is the clock's (sim clock in SIM mode). MOCK bars are not
      // bucket-aligned, so the offline sim's own tick count stands in (mock.fresh / mock.sinceNew below).
      const tfMin = TF_MIN[tf] || 1;
      const mock = { fresh: false, sinceNew: 0 };
      const formingOf: FormingOf = MOCK
        ? () => (mock.fresh ? { frac: Math.min(1, (mock.sinceNew + 1) / 14) } : null)
        : (bars) => {
          const last = bars[bars.length - 1];
          const now = Date.now();
          return live && last && isForming(last.time, tfMin, now) ? { frac: paceFrac(last.time * 1000, tfMin, now) } : null;
        };
      if (ind.volume) {
        const pulse = new VolPulse(chart, refs.current.buyscore ? 2 : 1, () => dataRef.current.candles, formingOf, rvBus.current!.emit);
        refs.current.rv = pulse.rv;          // series: the rebuild teardown removes them with the rest
        refs.current.rvPace = pulse.pace;
        pulseRef.current = pulse;
        pulse.update([], true);
        if (live && !MOCK) pulseClockRef.current = setInterval(() => { if (pulseRef.current === pulse) pulse.update(); }, 1000);
      }

      if (refs.current.buyscore || refs.current.rv) {
        try {
          const panes = chart.panes();
          // the pulse stays small (15 %): a strip under the candles, not a second chart
          if (panes.length >= 3) {
            if (panes[0]) panes[0].setStretchFactor(74);
            if (panes[1]) panes[1].setStretchFactor(11);
            if (panes[2]) panes[2].setStretchFactor(15);
          } else if (refs.current.rv) {
            if (panes[0]) panes[0].setStretchFactor(85);
            if (panes[1]) panes[1].setStretchFactor(15);
          } else {
            if (panes[0]) panes[0].setStretchFactor(78);
            if (panes[1]) panes[1].setStretchFactor(22);
          }
        } catch (e) {}
      }

      // ---- model (AI) fire flags — seeded from the ladder receipts log, live via
      // the ladder-signal channel; every non-model state falls out at FLAG_DEF ----
      if (ind.flags && !MOCK) {
        const step = (TF_MIN[tf] || 1) * 60;
        const markersApi = createSeriesMarkers(candle, []);
        const flags: any[] = [];
        const pushSig = (sig: any) => {
          if (!sig || sig.cls === 'tactical') return;
          // Model fires: chart shows only the very strong, tape-informed ones
          // (p≥0.90). The full stream stays on the ladder card + watchlist.
          if (sig.state === 'MV_HOLD_S' || sig.state === 'MV_HOLD_R') {
            const mv = sig.metrics && sig.metrics.mv;
            if (!mv || !(mv.p >= 0.9) || mv.tape !== true) return;
          }
          const def = FLAG_DEF[sig.state]; if (!def) return;
          const ts = Number(sig.ts); if (!Number.isFinite(ts)) return;
          let text = def.text;
          if (sig.state === 'MV_HOLD_S' || sig.state === 'MV_HOLD_R') {
            const mv = sig.metrics && sig.metrics.mv;
            // The level's OWN timeframe, not the chart's — a 5m EMA is the same
            // price on every pane, so the label reads identically everywhere.
            text = levelLabel(mv && mv.ind, mv && mv.family) || def.text;
          }
          flags.push({ time: Math.floor(ts / 1000 / step) * step, ...def, text, size: 1 });
        };
        const commit = () => {
          flags.sort((a, b) => a.time - b.time);
          if (flags.length > FLAGS_CAP) flags.splice(0, flags.length - FLAGS_CAP);
          // Collapse same-state markers on the same bar: a violent leg can break
          // two lids in seconds (both real signals — e.g. MIMI 04:18 premarket),
          // but one arrow per bar reads better. Rendered as "BC ×2".
          // Group by (bar, side, style): identical labels collapse to "×N" as
          // before, and genuinely different levels holding the same bar join
          // with "+" instead of stacking two markers on one spot. Keying on
          // colour+shape keeps unrelated signal states apart.
          const byKey = new Map<string, any>();
          for (const f of flags) {
            const k = `${f.time}|${f.position}|${f.color}|${f.shape}`;
            const cur = byKey.get(k);
            if (cur) cur._texts.push(f.text);
            else byKey.set(k, { ...f, _texts: [f.text] });
          }
          const merged = [...byKey.values()]
            .map((m) => ({ ...m, text: mergeLabels(m._texts) }))
            .sort((a, b) => a.time - b.time);
          try { markersApi.setMarkers(merged); } catch (e) { /* series gone mid-rebuild */ }
        };
        getLadder(symbol).then((resp: any) => {
          if (cancelled || !resp || !Array.isArray(resp.signals)) return;
          for (const sig of resp.signals) pushSig(sig); // log is newest-first; sort handles order
          commit();
        }).catch(() => { /* no ladder yet — flags arrive live */ });
        const offFlags = subscribeLadder(symbol, (f: any) => {
          if (f.type !== 'ladder-signal') return;
          pushSig(f); commit();
        });
        cleanupFlags = () => { offFlags(); try { markersApi.setMarkers([]); } catch (e) {} };
      }

      chart.timeScale().scrollToPosition(padBars(padRef.current), false);

      if (tickRef.current) clearInterval(tickRef.current);

      if (live && MOCK) {
        // ---- offline sim tick loop ----
        const m = META[symbol] || { base: candles[candles.length - 1].close, chg: 0 };
        const step = (TF_MIN[tf] || 1) * 60;
        const volStep = Math.max(0.002, 0.006 * Math.sqrt(dataRef.current.tfMin)) * m.base;
        tickRef.current = setInterval(() => {
          const arr = dataRef.current.candles;
          const rng = dataRef.current.rng;
          const cd = arr[arr.length - 1];
          const drift = (m.chg >= 0 ? 1 : -1) * volStep * 0.08;
          cd.close = Math.max(0.02, +(cd.close + (rng() - 0.5) * 2 * volStep + drift).toFixed(4));
          cd.high = Math.max(cd.high, cd.close);
          cd.low = Math.min(cd.low, cd.close);
          cd.up = cd.close >= cd.open;
          cd.value += Math.round(rng() * 4000 * dataRef.current.tfMin);
          candle.update({ time: cd.time, open: cd.open, high: cd.high, low: cd.low, close: cd.close });
          syncTag(cd.close);
          if (pulseRef.current) pulseRef.current.update([cd.time]);
          refreshTails(arr);
          mock.sinceNew++;
          if (mock.sinceNew >= 14) {
            mock.sinceNew = 0;
            const open = cd.close;
            const nc = { time: cd.time + step, open, high: open, low: open, close: open, value: Math.round(2000 + rng() * 6000), up: true };
            arr.push(nc);
            candle.update({ time: nc.time, open: nc.open, high: nc.high, low: nc.low, close: nc.close });
            syncTag(nc.close);
            mock.fresh = true;     // from the first new bar on, the last bar is the forming one
            if (pulseRef.current) pulseRef.current.update([nc.time]);
          }
          cbRef.current.onPrice && cbRef.current.onPrice(cd.close);
        }, 360);
      } else if (live && !MOCK) {
        // ---- live: the screener's forming bars + trade prints, stitched (lib/forming.ts) ----
        const render = (t: Touch) => {
          if (!t.times.length) return;
          const arr = dataRef.current.candles;
          if (t.reset) {
            candle.setData(arr.map((d: any) => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close })));
          } else {
            for (const time of t.times) {
              let i = arr.length - 1;
              while (i >= 0 && arr[i].time > time) i--;
              const cd = arr[i];
              if (!cd || cd.time !== time) continue;
              const historical = i < arr.length - 1;   // a bar behind the last one (lightweight-charts v5 `historicalUpdate`)
              candle.update({ time: cd.time, open: cd.open, high: cd.high, low: cd.low, close: cd.close }, historical);
            }
          }
          if (pulseRef.current) pulseRef.current.update(t.times, t.reset);
          refreshTails(arr);
          const last = arr[arr.length - 1];
          if (last) syncTag(last.close);
          if (last) cbRef.current.onPrice && cbRef.current.onPrice(last.close);
        };
        const lc = new LiveCandles(dataRef.current.candles, TF_MIN[tf] || 1);
        if (candles.length) cbRef.current.onPrice && cbRef.current.onPrice(candles[candles.length - 1].close);
        // the hydrate's snapshot, then everything that arrived while it loaded: prints first (they join the ring),
        // then frames (each rebuilds the forming candle from its values + the ring's prints after its watermark)
        // one bad frame must never leave the series behind the array (every later update would then throw): resync
        const safe = (fn: () => Touch) => {
          try { render(fn()); }
          catch (e) {
            console.warn('[chart] live update failed — resyncing the series', e);
            try { render({ times: [0], reset: true }); } catch (e2) { /* chart gone mid-rebuild */ }
          }
        };
        if (formingSec) safe(() => lc.applySnapshot(pickSnap({ asOf: formingSec.asOf, wm: formingSec.wm, forming: { [serverTf]: formingSec.forming }, lastClosed: { [serverTf]: formingSec.lastClosed } })));
        for (const p of early.prints.splice(0)) safe(() => lc.applyPrint(p));
        for (const sn of early.snaps.splice(0)) safe(() => lc.applySnapshot(sn));
        early.onPrint = (p) => safe(() => lc.applyPrint(p));
        early.onSnap = (sn) => safe(() => lc.applySnapshot(sn));
      } else if (candles.length) {
        cbRef.current.onPrice && cbRef.current.onPrice(candles[candles.length - 1].close);
      }
    })();

    return () => {
      cancelled = true;
      if (tickRef.current) clearInterval(tickRef.current);
      if (bsPollRef.current) { clearInterval(bsPollRef.current); bsPollRef.current = null; }
      if (pulseClockRef.current) { clearInterval(pulseClockRef.current); pulseClockRef.current = null; }
      cleanupLive();
      cleanupFlags();
    };
  }, [symbol, tf, indicators, live]);

  // overlays docked at the axis (S/R rail, entry-mark column) → keep the latest candles clear of them
  useEffect(() => { const ch = chartRef.current; if (ch && refs.current.candle) { try { ch.timeScale().scrollToPosition(padBars(padRight), false); } catch (e) {} } }, [padRight]);

  // ---- colors update ----
  useEffect(() => {
    const chart = chartRef.current; if (!chart) return;
    const c = colors;
    chart.applyOptions({ layout: { background: { color: c.bg }, textColor: c.text } } as any);
    if (refs.current.candle) refs.current.candle.applyOptions({ upColor: c.up, downColor: c.down, wickUpColor: c.up, wickDownColor: c.down });
  }, [colors]);

  return React.createElement(React.Fragment, null,
    React.createElement('div', { className: 'chart-host', ref: hostRef }),
    React.createElement(VolPulseReadout, { bus: rvBus.current, hostRef, chartRef, pulseRef }));
}
